/**
 * T155P1 — Review Inbox (Transaction Review Cockpit)
 *
 * Displays the 4 review buckets returned by GET /transactions/review-queue:
 *   • Uncategorized
 *   • Suspicious
 *   • Duplicate-like
 *   • Recurring
 *
 * Features:
 *   - Progress bar showing reviewed / total
 *   - Tab-per-bucket with live badge counts
 *   - Desktop table view (≥768px) + mobile card view (<768px)
 *   - Per-item: Edit (opens TransactionDetailSheet) + Mark Reviewed (PUT)
 *   - Desktop batch: checkboxes + "Mark all reviewed" (POST batch)
 *   - Slice 4: RuleApplyBanner + RulePreviewModal + RuleUndoBanner + RuleSuggestionChip
 */
import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, Inbox } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAutomationPreferences } from '@/hooks/useAutomationPreferences';
import PageGuide from '@/components/common/PageGuide';
import { reviewGuide } from '@/components/common/guideContent';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  fetchReviewQueue,
  batchMarkReviewed,
  removeItemFromQueue,
  removeItemsFromQueue,
} from '@/features/review/reviewSlice';
import { updateTransaction } from '@/features/transactions/transactionsSlice';
import { fetchAccounts } from '@/features/accounts/accountsSlice';
import { fetchCategories } from '@/features/categories/categoriesSlice';
import { fetchTransactionRules, commitAllRules, clearDryRunResult } from '@/features/transactionRules/transactionRulesSlice';
import { useCurrency } from '@/hooks/useCurrency';
import TransactionDetailSheet from '@/components/transactions/TransactionDetailSheet';
import { SuggestionChip } from '@/components/review/SuggestionChip';
import { RuleApplyBanner } from '@/components/review/RuleApplyBanner';
import { RulePreviewModal } from '@/components/review/RulePreviewModal';
import { RuleUndoBanner } from '@/components/review/RuleUndoBanner';
import { RuleSuggestionChip } from '@/components/review/RuleSuggestionChip';
import { ItemColumnToggle } from '@/components/transactions/ItemColumnToggle';
import type { Transaction } from '@/lib/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string, fmt: (d: Date) => string) {
  try {
    return fmt(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// Skeleton rows while loading
// ---------------------------------------------------------------------------

function TableSkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-4" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-40" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-8 w-28" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

function CardSkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-lg border p-4 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-8 w-32" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center py-16 text-center gap-3"
      data-testid="review-empty-state"
    >
      <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-4">
        <CheckCircle2 className="size-8 text-green-600 dark:text-green-400" />
      </div>
      <p className="text-lg font-semibold text-foreground">All caught up!</p>
      <p className="text-sm text-muted-foreground">Nothing needs review right now.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Amount badge
// ---------------------------------------------------------------------------

function AmountBadge({ amount, type }: { amount: number; type: string }) {
  const { fmt } = useCurrency();
  const colorClass =
    type === 'income'
      ? 'text-green-600 dark:text-green-400'
      : type === 'expense'
      ? 'text-red-600 dark:text-red-400'
      : 'text-blue-600 dark:text-blue-400';
  return (
    <span className={`font-medium tabular-nums ${colorClass}`}>
      {type === 'income' ? '+' : type === 'expense' ? '-' : ''}
      {fmt(Math.abs(amount))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// BucketTab — renders desktop table + mobile cards for one bucket
// ---------------------------------------------------------------------------

interface BucketTabProps {
  bucketKey: 'uncategorized' | 'suspicious' | 'duplicateLike' | 'recurringLinked';
  items: Transaction[];
  isLoading: boolean;
  onMarkReviewed: (id: string) => void;
  onEdit: (transaction: Transaction) => void;
  onBatchMarkReviewed: (ids: string[]) => void;
  markingIds: Set<string>;
  showSuggestions: boolean;
  /** Banner/chips are shown only for the uncategorized bucket */
  onOpenPreview?: () => void;
}

function BucketTab({
  bucketKey,
  items,
  isLoading,
  onMarkReviewed,
  onEdit,
  onBatchMarkReviewed,
  markingIds,
  showSuggestions,
  onOpenPreview,
}: BucketTabProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(items.map((t) => t.id)) : new Set());
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  // Clear stale selection when items list changes
  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(items.map((t) => t.id));
      const cleaned = new Set([...prev].filter((id) => validIds.has(id)));
      return cleaned.size !== prev.size ? cleaned : prev;
    });
  }, [items]);

  if (isLoading) {
    return (
      <>
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody><TableSkeletonRows /></TableBody>
          </Table>
        </div>
        <div className="md:hidden"><CardSkeletonList /></div>
      </>
    );
  }

  if (items.length === 0) {
    return <EmptyState />;
  }

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <>
      {/* Rule Apply Banner — uncategorized bucket only */}
      {bucketKey === 'uncategorized' && showSuggestions && onOpenPreview && (
        <div className="mb-3">
          <RuleApplyBanner itemCount={items.length} onOpenPreview={onOpenPreview} />
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block">
        {/* Batch toolbar */}
        <div className="flex items-center justify-between py-2 px-1 gap-2">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allSelected}
              ref={(el) => { if (el) (el as HTMLButtonElement).dataset.indeterminate = someSelected ? 'true' : 'false'; }}
              onCheckedChange={(checked) => handleSelectAll(!!checked)}
              aria-label="Select all"
              className="border-border"
            />
            {selectedIds.size > 0 && (
              <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={selectedIds.size === 0}
            onClick={() => onBatchMarkReviewed(Array.from(selectedIds))}
            data-testid="review-batch-mark-reviewed"
          >
            <CheckCircle2 className="size-4 mr-1.5" />
            Mark {selectedIds.size > 0 ? `${selectedIds.size} ` : 'all '}reviewed
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Merchant</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((txn) => (
              <TableRow key={txn.id} data-testid={`review-item-${txn.id}`}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(txn.id)}
                    onCheckedChange={(checked) => handleSelectOne(txn.id, !!checked)}
                    aria-label={`Select ${txn.description}`}
                  />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatDate(txn.date, (d) => d.toLocaleDateString())}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="font-medium text-sm line-clamp-1">{txn.description}</span>
                    {bucketKey === 'uncategorized' && showSuggestions && txn.merchant && (
                      <SuggestionChip
                        transaction={txn}
                        onApply={(categoryId, categoryName) => {
                          // Apply suggestion = set category + mark reviewed
                          void (async () => {
                            // The parent's onMarkReviewed will handle optimistic removal
                            // after the PUT succeeds; we piggyback categoryId in the update
                            onEdit({ ...txn, categoryId, category: categoryName } as Transaction);
                          })();
                        }}
                        onEdit={(suggestedCategoryId, suggestedCategoryName) => {
                          onEdit({ ...txn, categoryId: suggestedCategoryId, category: suggestedCategoryName } as Transaction);
                        }}
                      />
                    )}
                    {bucketKey === 'uncategorized' && showSuggestions && (
                      <RuleSuggestionChip
                        transactionId={txn.id}
                        onApply={(_categoryId, categoryName) => {
                          onEdit({ ...txn, category: categoryName } as Transaction);
                        }}
                        onEdit={(_categoryId, categoryName) => {
                          onEdit({ ...txn, category: categoryName } as Transaction);
                        }}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <AmountBadge amount={txn.amount} type={txn.type} />
                </TableCell>
                <TableCell className="text-sm">
                  {txn.category ? (
                    <Badge variant="outline" className="text-xs">{typeof txn.category === 'string' ? txn.category : (txn.category as any)?.name}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs italic">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {typeof txn.merchant === 'string' ? (txn.merchant ?? '—') : ((txn.merchant as any)?.name ?? '—')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => onEdit(txn)}
                      data-testid={`review-edit-${txn.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs text-green-600 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-950/30"
                      disabled={markingIds.has(txn.id)}
                      onClick={() => onMarkReviewed(txn.id)}
                      data-testid={`review-mark-reviewed-${txn.id}`}
                    >
                      <CheckCircle2 className="size-3.5 mr-1" />
                      {markingIds.has(txn.id) ? 'Marking…' : 'Reviewed'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {items.map((txn) => (
          <div
            key={txn.id}
            className="rounded-lg border bg-card p-4 space-y-2"
            data-testid={`review-item-${txn.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-sm break-words">{txn.description}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatDate(txn.date, (d) => d.toLocaleDateString())}
                  {txn.merchant ? ` · ${typeof txn.merchant === 'string' ? txn.merchant : (txn.merchant as any)?.name}` : ''}
                </p>
              </div>
              <AmountBadge amount={txn.amount} type={txn.type} />
            </div>

            {txn.category && (
              <Badge variant="outline" className="text-xs">{typeof txn.category === 'string' ? txn.category : (txn.category as any)?.name}</Badge>
            )}

            {bucketKey === 'uncategorized' && showSuggestions && txn.merchant && (
              <SuggestionChip
                transaction={txn}
                onApply={(categoryId, categoryName) => {
                  onEdit({ ...txn, categoryId, category: categoryName } as Transaction);
                }}
                onEdit={(suggestedCategoryId, suggestedCategoryName) => {
                  onEdit({ ...txn, categoryId: suggestedCategoryId, category: suggestedCategoryName } as Transaction);
                }}
              />
            )}
            {bucketKey === 'uncategorized' && showSuggestions && (
              <RuleSuggestionChip
                transactionId={txn.id}
                onApply={(_categoryId, categoryName) => {
                  onEdit({ ...txn, category: categoryName } as Transaction);
                }}
                onEdit={(_categoryId, categoryName) => {
                  onEdit({ ...txn, category: categoryName } as Transaction);
                }}
              />
            )}

            <div className="flex items-center gap-2 pt-1 min-h-[44px]">
              <Button
                variant="ghost"
                size="sm"
                className="h-9 flex-1 text-xs"
                onClick={() => onEdit(txn)}
                data-testid={`review-edit-${txn.id}`}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-9 flex-1 text-xs text-green-600 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-950/30"
                disabled={markingIds.has(txn.id)}
                onClick={() => onMarkReviewed(txn.id)}
                data-testid={`review-mark-reviewed-${txn.id}`}
              >
                <CheckCircle2 className="size-3.5 mr-1" />
                {markingIds.has(txn.id) ? 'Marking…' : 'Reviewed'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function Review() {
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const queue = useAppSelector((s) => s.review.queue);
  const isLoading = useAppSelector((s) => s.review.isLoading);
  const accounts = useAppSelector((s) => s.accounts.accounts);
  const categories = useAppSelector((s) => s.categories.categories);

  // Tracking which items are mid-flight for mark-reviewed
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());

  // Preview modal open state
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    showSuggestions,
    requirePreview,
  } = useAutomationPreferences();
  const [manualSuggestionOverride, setManualSuggestionOverride] = useState(false);

  // Edit sheet state
  const [editSheet, setEditSheet] = useState<{ open: boolean; transaction: Transaction | null }>({
    open: false,
    transaction: null,
  });

  // Load data
  useEffect(() => {
    void dispatch(fetchReviewQueue());
    void dispatch(fetchAccounts());
    void dispatch(fetchCategories());
    void dispatch(fetchTransactionRules());
  }, [dispatch]);

  // Progress calculation: items remaining = total items in queue
  const remaining = queue?.total ?? 0;

  // We don't have a "completed" counter from the API, so show total pending
  // Progress bar shows how many items have been cleared THIS session
  const [sessionCleared, setSessionCleared] = useState(0);
  const sessionTotal = (queue?.total ?? 0) + sessionCleared;
  const progressPct = sessionTotal > 0 ? Math.round((sessionCleared / sessionTotal) * 100) : 0;

  const handleMarkReviewed = useCallback(
    async (id: string) => {
      setMarkingIds((prev) => new Set([...prev, id]));
      try {
        await dispatch(updateTransaction({ id, data: { needsReview: false } })).unwrap();
        dispatch(removeItemFromQueue(id));
        setSessionCleared((c) => c + 1);
      } catch {
        toast({ title: 'Failed to mark as reviewed', variant: 'destructive' });
      } finally {
        setMarkingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [dispatch, toast]
  );

  const handleBatchMarkReviewed = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      try {
        await dispatch(batchMarkReviewed({ transactionIds: ids })).unwrap();
        dispatch(removeItemsFromQueue(ids));
        setSessionCleared((c) => c + ids.length);
        toast({ title: `Marked ${ids.length} transaction${ids.length !== 1 ? 's' : ''} as reviewed` });
      } catch {
        toast({ title: 'Failed to mark transactions as reviewed', variant: 'destructive' });
      }
    },
    [dispatch, toast]
  );

  const handleOpenEdit = useCallback((transaction: Transaction) => {
    setEditSheet({ open: true, transaction });
  }, []);

  const handleSheetClose = useCallback(
    (open: boolean) => {
      if (!open) {
        setEditSheet({ open: false, transaction: null });
        // Refresh queue after an edit to pick up category changes
        void dispatch(fetchReviewQueue());
      }
    },
    [dispatch]
  );

  const handleEditSave = useCallback(
    async (transaction: Transaction) => {
      // When user saves from the edit sheet, also mark as reviewed
      await handleMarkReviewed(transaction.id);
    },
    [handleMarkReviewed]
  );

  // T155P4 — Commit rules from preview modal
  const handleCommitRules = useCallback(async () => {
    try {
      await dispatch(commitAllRules()).unwrap();
      setPreviewOpen(false);
      toast({ title: 'Rules applied successfully' });
      // Refresh queue to reflect category changes
      void dispatch(fetchReviewQueue());
    } catch {
      toast({ title: 'Failed to apply rules', variant: 'destructive' });
    }
  }, [dispatch, toast]);

  // T155P4 — After undo, refresh queue and clear dryRun state
  const handleUndone = useCallback(() => {
    void dispatch(clearDryRunResult());
    void dispatch(fetchReviewQueue());
    // Allow re-check next time user visits uncategorized tab
    try { sessionStorage.removeItem('fintrack:rule-banner-session-checked'); } catch { /* ignore */ }
  }, [dispatch]);

  const buckets = [
    { key: 'uncategorized' as const, label: 'Uncategorized', count: queue?.uncategorized.count ?? 0, testId: 'review-tab-uncategorized' },
    { key: 'suspicious' as const, label: 'Suspicious', count: queue?.suspicious.count ?? 0, testId: 'review-tab-suspicious' },
    { key: 'duplicateLike' as const, label: 'Duplicate-like', count: queue?.duplicateLike.count ?? 0, testId: 'review-tab-duplicate' },
    { key: 'recurringLinked' as const, label: 'Recurring', count: queue?.recurringLinked.count ?? 0, testId: 'review-tab-recurring' },
  ];
  const effectiveShowSuggestions =
    manualSuggestionOverride
      ? true
      : showSuggestions;

  return (
    <div className="flex flex-col gap-6 pb-6" data-testid="review-page">
      <div data-testid="review-page-guide">
        <PageGuide pageKey="review" content={reviewGuide} />
      </div>
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Inbox className="size-5 text-muted-foreground" />
          <h1 className="text-2xl font-bold tracking-tight">Review Inbox</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {isLoading
            ? 'Loading transactions…'
            : remaining > 0
            ? `${remaining} transaction${remaining !== 1 ? 's' : ''} need your attention`
            : 'All caught up!'}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {!effectiveShowSuggestions && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px] md:min-h-0"
            data-testid="review-speed-mode-show-suggestions"
            onClick={() => setManualSuggestionOverride(true)}
          >
            Show Suggestions
          </Button>
        )}
        {manualSuggestionOverride && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-[44px] md:min-h-0"
            data-testid="review-suggestions-override-clear-btn"
            onClick={() => setManualSuggestionOverride(false)}
          >
            Clear Override
          </Button>
        )}
        <ItemColumnToggle page="review" />
      </div>

      {!requirePreview && (
        <p className="text-xs text-muted-foreground">
          Preview-first remains enabled for bulk rule commits in Review.
        </p>
      )}

      {/* Progress bar */}
      <div className="space-y-1.5" data-testid="review-progress-bar">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Session progress</span>
          <span>{sessionCleared} reviewed this session</span>
        </div>
        <Progress value={progressPct} className="h-2" />
      </div>

      {/* Undo banner — shown after a successful rule commit */}
      <RuleUndoBanner onUndone={handleUndone} />

      {/* Bucket tabs */}
      <Tabs defaultValue="uncategorized">
        <TabsList className="flex-wrap h-auto gap-1">
          {buckets.map((b) => (
            <TabsTrigger
              key={b.key}
              value={b.key}
              className="flex items-center gap-1.5 min-h-[44px]"
              data-testid={b.testId}
            >
              {b.label}
              {(isLoading || b.count > 0) && (
                <Badge
                  variant={b.count > 0 ? 'destructive' : 'secondary'}
                  className="text-[10px] px-1.5 py-0 min-w-[1.25rem] h-4"
                >
                  {isLoading ? '…' : b.count}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {buckets.map((b) => (
          <TabsContent key={b.key} value={b.key} className="mt-4">
            <BucketTab
              bucketKey={b.key}
              items={queue?.[b.key].items ?? []}
              isLoading={isLoading}
              onMarkReviewed={handleMarkReviewed}
              onEdit={handleOpenEdit}
              onBatchMarkReviewed={handleBatchMarkReviewed}
              markingIds={markingIds}
              showSuggestions={effectiveShowSuggestions}
              onOpenPreview={b.key === 'uncategorized' ? () => setPreviewOpen(true) : undefined}
            />
          </TabsContent>
        ))}
      </Tabs>

      {/* Preview modal */}
      <RulePreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        onConfirm={handleCommitRules}
      />

      {/* Inline edit sheet */}
      <TransactionDetailSheet
        transaction={editSheet.transaction}
        open={editSheet.open}
        onOpenChange={handleSheetClose}
        accounts={accounts}
        categories={categories}
        onEdit={handleEditSave}
        onRefresh={() => void dispatch(fetchReviewQueue())}
      />
    </div>
  );
}
